import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, Loader2, Users, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { PatolaBorder } from '../components/luxury/PatolaBorder';
import { api } from '../App';

export default function SaturdayClubSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const registrationId = searchParams.get('registration_id');
  const [status, setStatus] = useState('polling');
  const [paymentData, setPaymentData] = useState(null);
  const [registration, setRegistration] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    const poll = async () => {
      try {
        const res = await api.get(`/api/payments/status/${sessionId}`);
        if (res.payment_status === 'paid') {
          setPaymentData(res);
          setStatus('paid');
          if (registrationId) {
            api.get(`/api/saturday-club/registration/${registrationId}`).then(setRegistration).catch(() => {});
          }
          return;
        }
        if (res.status === 'expired') { setStatus('expired'); return; }
        if (attempts < 10) setTimeout(() => setAttempts(a => a + 1), 2000); else setStatus('timeout');
      } catch {
        if (attempts < 10) setTimeout(() => setAttempts(a => a + 1), 2000); else setStatus('error');
      }
    };
    poll();
  }, [sessionId, registrationId, attempts]);

  return (
    <div className="min-h-[70vh] bg-[hsl(var(--lux-parchment))] py-16">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <Card className="lux-ornate-card border-2 border-[hsl(var(--lux-gold))]">
          <PatolaBorder />
          <CardContent className="p-8 text-center">
            {status === 'polling' && (
              <div data-testid="saturday-club-success-polling">
                <Loader2 className="w-12 h-12 animate-spin text-[hsl(var(--lux-royal))] mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-[hsl(var(--lux-royal))] mb-2" style={{ fontFamily: 'Cormorant Garamond' }}>Confirming your payment…</h2>
                <p className="text-[hsl(var(--lux-ink)/0.7)]">Please wait a moment while we confirm with Stripe.</p>
              </div>
            )}

            {status === 'paid' && (
              <div data-testid="saturday-club-success-page">
                <div className="w-16 h-16 rounded-full bg-[hsl(var(--lux-royal-soft))] flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-8 h-8 text-[hsl(var(--lux-royal))]" />
                </div>
                <h1 className="text-3xl font-bold text-[hsl(var(--lux-royal))] mb-2" style={{ fontFamily: 'Cormorant Garamond' }}>Payment received — thank you!</h1>
                <p className="text-[hsl(var(--lux-ink)/0.75)] mb-6">
                  {registration?.child_name ? <>Saturday Club fees for <strong>{registration.child_name}</strong> are paid.</> : 'Your Saturday Club fees are paid.'} A receipt has been emailed to you.
                </p>
                {paymentData && (
                  <div className="inline-block px-8 py-5 rounded-xl bg-[hsl(var(--lux-royal-soft))] mb-6">
                    <div className="text-sm text-[hsl(var(--lux-ink)/0.7)]">Amount paid</div>
                    <div className="text-4xl font-bold text-[hsl(var(--lux-gold))] tabular-nums" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="saturday-club-success-amount">
                      £{(paymentData.amount_total / 100).toFixed(2)}
                    </div>
                    {registrationId && <div className="text-xs font-mono text-[hsl(var(--lux-ink)/0.6)] mt-1">Ref {registrationId}</div>}
                  </div>
                )}
                <div className="flex flex-wrap gap-3 justify-center">
                  <Link to="/saturday-club"><Button variant="outline" className="border-[hsl(var(--lux-gold)/0.55)] text-[hsl(var(--lux-royal))]"><Users className="w-4 h-4 mr-2" />Saturday Club</Button></Link>
                  <Link to="/"><Button className="bg-[hsl(var(--lux-royal))] text-white hover:bg-[hsl(var(--lux-royal-2))]">Back to Home</Button></Link>
                </div>
              </div>
            )}

            {status !== 'polling' && status !== 'paid' && (
              <div data-testid="saturday-club-success-error">
                <div className="w-16 h-16 rounded-full bg-[hsl(var(--lux-gold-soft))] flex items-center justify-center mx-auto mb-6">
                  <AlertTriangle className="w-8 h-8 text-[hsl(var(--lux-gold))]" />
                </div>
                <h2 className="text-2xl font-bold text-[hsl(var(--lux-royal))] mb-2" style={{ fontFamily: 'Cormorant Garamond' }}>We could not confirm the payment yet</h2>
                <p className="text-[hsl(var(--lux-ink)/0.7)] mb-6">
                  If your card was charged, your registration will be marked paid automatically once Stripe confirms — you do not need to pay again.
                  {registrationId && <> Your reference is <span className="font-mono">{registrationId}</span>.</>}
                </p>
                <div className="flex flex-wrap gap-3 justify-center">
                  <Link to={registrationId ? `/saturday-club?registration_id=${registrationId}` : '/saturday-club'}>
                    <Button className="bg-[hsl(var(--lux-royal))] text-white hover:bg-[hsl(var(--lux-royal-2))]">Check registration</Button>
                  </Link>
                  <Link to="/contact"><Button variant="outline">Contact us</Button></Link>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
